// ============================================================
// FIXED: StaffPerformanceReportPage.tsx
// ============================================================
// src/features/reports/pages/StaffPerformanceReportPage.tsx

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { getBranches } from '@/features/settings/services/settingsApi';
import { getStaffPerformanceReport, getExportUrl } from '../services/reportsApi';
import { ReportHeader } from '../components/ReportHeader';
import { ReportFilters } from '../components/ReportFilters';
import { SummaryCard } from '../components/SummaryCard';
import {
    Users, DollarSign, ShoppingCart, TrendingUp,
    UserCheck, RotateCcw, Clock, Target,
    Award, Zap, BarChart3
} from 'lucide-react';
import { motion } from 'framer-motion';

export function StaffPerformanceReportPage() {
    const userAuth = useAuthStore((s) => s.user);
    const tenantId = userAuth?.tenantId || '';

    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    const [branchId, setBranchId] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [sortBy, setSortBy] = useState<'revenue' | 'sales' | 'itemsSold'>('revenue');

    const { data: branches } = useQuery({
        queryKey: ['branches-report', tenantId],
        queryFn: () => getBranches(tenantId),
        enabled: !!tenantId,
    });

    const params = {
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        branchId: branchId || undefined,
    };

    const { data, isLoading } = useQuery({
        queryKey: ['report-staff-performance', tenantId, params],
        queryFn: () => getStaffPerformanceReport(tenantId, params),
        enabled: !!tenantId,
    });

    const formatNaira = (val: string | number) =>
        `₦${parseFloat(String(val || '0')).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    const formatNumber = (val: string | number) => parseFloat(String(val || '0')).toLocaleString();

    const branchesList = (branches || []) as any[];

    const setQuickDate = (days: number) => {
        const end = new Date();
        const start = new Date();
        if (days === 0) start.setHours(0, 0, 0, 0);
        else start.setDate(start.getDate() - days);
        setDateRange({ startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] });
    };

    const handleExport = (format: 'pdf' | 'excel') => {
        const url = getExportUrl(tenantId, 'staff_performance', format, params as any);
        window.open(url, '_blank');
    };

    const hasFilters = !!(dateRange.startDate || dateRange.endDate || branchId);
    const clearFilters = () => { setDateRange({ startDate: '', endDate: '' }); setBranchId(''); };

    const staff = data?.performance || [];
    const sortedStaff = [...staff].sort((a: any, b: any) => {
        if (sortBy === 'revenue') return parseFloat(b.totalRevenue) - parseFloat(a.totalRevenue);
        if (sortBy === 'sales') return b.totalSales - a.totalSales;
        if (sortBy === 'itemsSold') return b.totalItems - a.totalItems;
        return 0;
    });

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30">
            <ReportHeader
                title="Staff Performance"
                description="Employee sales analytics"
                icon={Users}
                iconGradient="from-indigo-500 to-purple-600"
                hasFilters={hasFilters}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                onExportPdf={() => handleExport('pdf')}
                onExportExcel={() => handleExport('excel')}
                branchName={branchesList.find(b => b.id === branchId)?.name}
            />

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                <ReportFilters
                    visible={showFilters}
                    dateRange={dateRange}
                    branchId={branchId}
                    branches={branchesList}
                    onDateRangeChange={setDateRange}
                    onBranchChange={setBranchId}
                    onClear={clearFilters}
                    onQuickDate={setQuickDate}
                />

                {isLoading ? (
                    <div className="flex justify-center py-20"><Spinner size="xl" /></div>
                ) : !data || staff.length === 0 ? (
                    <EmptyState
                        icon={<Users className="w-10 h-10" />}
                        title="No Staff Data"
                        description="No staff performance data found for the selected period."
                        size="lg"
                    />
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <SummaryCard
                                label="Active Staff"
                                value={String(data.summary?.totalStaff || 0)}
                                icon={Users}
                                gradient="from-indigo-500 to-purple-600"
                            />
                            <SummaryCard
                                label="Total Revenue"
                                value={formatNaira(data.summary?.totalRevenue || 0)}
                                icon={DollarSign}
                                gradient="from-emerald-500 to-teal-600"
                            />
                            <SummaryCard
                                label="Total Sales"
                                value={formatNumber(data.summary?.totalSales || 0)}
                                icon={ShoppingCart}
                                gradient="from-blue-500 to-indigo-600"
                            />
                            <SummaryCard
                                label="Total Voids"
                                value={formatNumber(data.summary?.totalVoids || 0)}
                                icon={RotateCcw}
                                gradient="from-rose-500 to-pink-600"
                            />
                            <SummaryCard
                                label="Top Performer"
                                value={data.summary?.topPerformer || 'N/A'}
                                icon={Award}
                                gradient="from-amber-500 to-orange-600"
                            />
                        </div>

                        {/* Performance Leaderboard */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                            {sortedStaff.slice(0, 3).map((staffMember: any, i: number) => {
                                const rankColors = [
                                    'border-amber-400 bg-amber-50/50',
                                    'border-slate-300 bg-slate-50/50',
                                    'border-orange-400 bg-orange-50/50',
                                ];
                                const rankBadges = [
                                    'bg-amber-500 text-white',
                                    'bg-slate-400 text-white',
                                    'bg-orange-500 text-white',
                                ];

                                return (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: i * 0.1 }}
                                    >
                                        <Card className={`border-2 ${rankColors[i]}`}>
                                            <CardContent className="p-5">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className={`w-8 h-8 rounded-full ${rankBadges[i]} flex items-center justify-center text-xs font-bold`}>
                                                        #{i + 1}
                                                    </div>
                                                    <Award className={`w-5 h-5 ${i === 0 ? 'text-amber-500' : i === 1 ? 'text-slate-400' : 'text-orange-500'
                                                        }`} />
                                                </div>
                                                <h3 className="text-lg font-bold text-slate-900">{staffMember.name}</h3>
                                                <p className="text-2xl font-black text-slate-900 mt-2">
                                                    {formatNaira(staffMember.totalRevenue)}
                                                </p>
                                                <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-slate-100">
                                                    <div>
                                                        <p className="text-xs text-slate-500">Sales</p>
                                                        <p className="text-sm font-bold">{staffMember.totalSales}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-slate-500">Items</p>
                                                        <p className="text-sm font-bold">{formatNumber(staffMember.totalItems)}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-slate-500">Avg Sale</p>
                                                        <p className="text-sm font-bold">{formatNaira(staffMember.avgTransactionValue)}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-slate-500">Per Hour</p>
                                                        <p className="text-sm font-bold">{formatNaira(staffMember.revenuePerHour)}</p>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                );
                            })}
                        </div>

                        {/* Sort Controls */}
                        <div className="flex gap-2">
                            <button
                                onClick={() => setSortBy('revenue')}
                                className={`px-3 py-1.5 text-xs font-medium rounded-lg border ${sortBy === 'revenue' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'border-slate-200 text-slate-600'
                                    }`}
                            >
                                Sort by Revenue
                            </button>
                            <button
                                onClick={() => setSortBy('sales')}
                                className={`px-3 py-1.5 text-xs font-medium rounded-lg border ${sortBy === 'sales' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'border-slate-200 text-slate-600'
                                    }`}
                            >
                                Sort by Sales
                            </button>
                            <button
                                onClick={() => setSortBy('itemsSold')}
                                className={`px-3 py-1.5 text-xs font-medium rounded-lg border ${sortBy === 'itemsSold' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'border-slate-200 text-slate-600'
                                    }`}
                            >
                                Sort by Items
                            </button>
                        </div>

                        <Card className="overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-100">
                                    <thead>
                                        <tr className="bg-slate-50/50">
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Staff</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Sales</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Revenue</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Avg Sale</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Items Sold</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Items/Sale</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Per Hour</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Voids</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Refunds</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Shifts</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Hours</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {sortedStaff.map((staffMember: any, i: number) => {
                                            const voidRate = staffMember.totalSales + staffMember.voidCount > 0
                                                ? (staffMember.voidCount / (staffMember.totalSales + staffMember.voidCount) * 100).toFixed(1)
                                                : '0';

                                            return (
                                                <tr key={i} className="hover:bg-slate-50 transition-colors">
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                                                                <span className="text-xs font-bold text-indigo-600">
                                                                    {staffMember.name?.charAt(0) || '?'}
                                                                </span>
                                                            </div>
                                                            <span className="text-sm font-medium text-slate-900">{staffMember.name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center text-sm font-semibold">
                                                        {formatNumber(staffMember.totalSales)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm font-bold text-slate-900">
                                                        {formatNaira(staffMember.totalRevenue)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm">
                                                        {formatNaira(staffMember.avgTransactionValue)}
                                                    </td>
                                                    <td className="px-6 py-4 text-center text-sm">
                                                        {formatNumber(staffMember.totalItems)}
                                                    </td>
                                                    <td className="px-6 py-4 text-center text-sm">
                                                        {staffMember.itemsPerSale || '0'}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm font-bold text-emerald-600">
                                                        {formatNaira(staffMember.revenuePerHour || '0')}
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <div>
                                                            <span className="text-sm text-rose-600 font-medium">{staffMember.voidCount}</span>
                                                            <span className="text-xs text-slate-400 ml-1">({voidRate}%)</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <span className="text-sm text-amber-600 font-medium">{staffMember.refundCount}</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-center text-sm">
                                                        {staffMember.shiftCount || '0'}
                                                    </td>
                                                    {/* FIXED: Safe toFixed call */}
                                                    <td className="px-6 py-4 text-center text-sm text-slate-500">
                                                        {(Number(staffMember.totalShiftHours) || 0).toFixed(1)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
}